// "Sign in with Google": the browser half.
//
// The backend's callback sends the browser to /?login_code=<code> on success or
// /?error=<reason> on refusal. The code is single-use, lasts 60 seconds and is
// traded for the session JWT in a POST body (see hooks/useAuth.js), so the JWT
// never appears in a URL. The retired SSO bridge put it in ?sso_token=, which
// landed in nginx access logs.

// Called ONCE, before React renders (src/index.js). It has to run first: the
// router sends "/" to "/login" for a signed-out visitor and would drop the query
// string. The parameters are stripped straight away so the code never lingers
// in the address bar or in history, and can never be spent twice.
export function takeLoginRedirect() {
  const params = new URLSearchParams(window.location.search);
  const loginCode = params.get('login_code');
  const error = params.get('error');
  if (loginCode !== null || error !== null) {
    params.delete('login_code');
    params.delete('error');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
  }
  return { loginCode, error };
}

// A plain link: the backend redirects on to Google's consent screen
export const GOOGLE_SIGN_IN_URL = `${process.env.REACT_APP_API_URL || '/api'}/auth/google`;

// One message per reason the backend can send (routes/google-auth.js)
const MESSAGES = {
  not_staff: "That Google account isn't set up for ClinicDesk. Ask an admin to add you using your @tapgp.co.uk address.",
  inactive: 'Your ClinicDesk account has been deactivated. Ask an admin if you think this is a mistake.',
  wrong_domain: 'Please sign in with your @tapgp.co.uk Google account.',
  unverified: "Google hasn't verified that account's email address, so it can't be used to sign in.",
  google_id_mismatch: 'This ClinicDesk account is linked to a different Google account. Ask an admin for help.',
  ambiguous_account: 'More than one ClinicDesk account matches that address. Ask an admin to sort it out.',
  state: 'Your sign-in expired or was started in a different browser. Please try again.',
  exchange_failed: "Google sign-in didn't complete. Please try again.",
  invalid_code: 'That sign-in link has expired or has already been used. Please sign in again.',
  rate_limited: 'Too many sign-in attempts. Please wait 15 minutes and try again.',
  server_error: 'Something went wrong while signing you in. Please try again.',
};

export const signInErrorMessage = (reason) => MESSAGES[reason] || 'Sign-in failed. Please try again.';
